import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import type { Executor, InsertClientWithExecutors } from "@shared/schema";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const CANADIAN_PROVINCES = [
  "AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"
];

interface ClientFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: InsertClientWithExecutors) => void;
  editMode?: boolean;
  editData?: Partial<InsertClientWithExecutors>;
  isPending?: boolean;
}

export function ClientFormDialog({ open, onOpenChange, onSubmit, editMode = false, editData, isPending = false }: ClientFormDialogProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("US");
  const [executors, setExecutors] = useState<Executor[]>([
    { name: "", relationship: "", email: "", phone: "", accessGranted: false }
  ]);

  useEffect(() => {
    if (editMode && editData && open) {
      setName(editData.name || "");
      setEmail(editData.email || "");
      setPhone(editData.phone || "");
      setAddress(editData.address || "");
      setCity(editData.city || "");
      setState(editData.state || "");
      setZipCode(editData.zipCode || "");
      setCountry(editData.country || "US");
    } else if (!open) {
      resetForm();
    }
  }, [editMode, editData, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const validExecutors = executors.filter(ex => {
      return ex.name.trim() !== "" && ex.email.trim() !== "";
    });

    const clientData: InsertClientWithExecutors = {
      name,
      email,
      phone,
      address,
      city,
      state,
      zipCode,
      country,
    };

    if (validExecutors.length > 0) {
      clientData.executors = validExecutors;
    }

    onSubmit?.(clientData);
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setCity("");
    setState("");
    setZipCode("");
    setCountry("US");
    setExecutors([{ name: "", relationship: "", email: "", phone: "", accessGranted: false }]);
  };

  const getStateLabel = () => country === "CA" ? "Province" : "State";
  const getZipLabel = () => country === "CA" ? "Postal Code" : "ZIP Code";
  const getStatePlaceholder = () => country === "CA" ? "ON" : "NY";
  const getZipPlaceholder = () => country === "CA" ? "K1A 0B1" : "10001";
  const getStateOptions = () => country === "CA" ? CANADIAN_PROVINCES : US_STATES;

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    setState("");
    setZipCode("");
  };

  const addExecutor = () => {
    setExecutors([...executors, { name: "", relationship: "", email: "", phone: "", accessGranted: false }]);
  };

  const removeExecutor = (index: number) => {
    setExecutors(executors.filter((_, i) => i !== index));
  };

  const updateExecutor = (index: number, field: keyof Executor, value: string | boolean) => {
    const updated = [...executors];
    updated[index] = { ...updated[index], [field]: value };
    setExecutors(updated);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]" data-testid={editMode ? "dialog-edit-client" : "dialog-new-client"}>
        <DialogHeader>
          <DialogTitle>{editMode ? "Edit Client Information" : "Create New Client"}</DialogTitle>
          <DialogDescription>
            {editMode ? "Update client contact details below." : "Add comprehensive client information including contact details and executor information."}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          <form onSubmit={handleSubmit} id="client-form">
            <div className="space-y-6 py-4">
              <div>
                <h3 className="text-sm font-semibold mb-3">Client Information</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="John Smith"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      data-testid="input-client-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="input-client-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Cell Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      data-testid="input-client-phone"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-sm font-semibold mb-3">Address Information</h3>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Select value={country} onValueChange={handleCountryChange}>
                      <SelectTrigger id="country" data-testid="select-country">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="US">United States</SelectItem>
                        <SelectItem value="CA">Canada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      placeholder="123 Main Street"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      required
                      data-testid="input-client-address"
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        placeholder={country === "CA" ? "Toronto" : "New York"}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                        data-testid="input-client-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">{getStateLabel()} *</Label>
                      <Select value={state} onValueChange={setState}>
                        <SelectTrigger id="state" data-testid="select-client-state">
                          <SelectValue placeholder={`Select ${getStateLabel().toLowerCase()}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {getStateOptions().map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zipCode">{getZipLabel()} *</Label>
                      <Input
                        id="zipCode"
                        placeholder={getZipPlaceholder()}
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        required
                        data-testid="input-client-zip"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {!editMode && (
                <>
                  <Separator />

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold">Executor(s) Information</h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addExecutor}
                        className="gap-2"
                        data-testid="button-add-executor"
                      >
                        <Plus className="h-4 w-4" />
                        Add Executor
                      </Button>
                    </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Executors must have both a name and email address to be saved.
                </p>
                <div className="space-y-4">
                  {executors.map((executor, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Executor {index + 1}</span>
                        {executors.length > 1 && (
                          <Button aria-label="Remove executor"
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => removeExecutor(index)}
                            data-testid={`button-remove-executor-${index}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor={`executor-name-${index}`}>Name *</Label>
                          <Input
                            id={`executor-name-${index}`}
                            placeholder="Jane Doe"
                            value={executor.name}
                            onChange={(e) => updateExecutor(index, "name", e.target.value)}
                            data-testid={`input-executor-name-${index}`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`executor-relationship-${index}`}>Relationship</Label>
                          <Input
                            id={`executor-relationship-${index}`}
                            placeholder="Spouse, Child, Attorney, etc."
                            value={executor.relationship}
                            onChange={(e) => updateExecutor(index, "relationship", e.target.value)}
                            data-testid={`input-executor-relationship-${index}`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`executor-email-${index}`}>Email *</Label>
                          <Input
                            id={`executor-email-${index}`}
                            type="email"
                            placeholder="jane@example.com"
                            value={executor.email}
                            onChange={(e) => updateExecutor(index, "email", e.target.value)}
                            data-testid={`input-executor-email-${index}`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`executor-phone-${index}`}>Phone</Label>
                          <Input
                            id={`executor-phone-${index}`}
                            type="tel"
                            placeholder="(555) 987-6543"
                            value={executor.phone}
                            onChange={(e) => updateExecutor(index, "phone", e.target.value)}
                            data-testid={`input-executor-phone-${index}`}
                          />
                        </div>
                      </div>
                      
                      <Separator className="my-3" />
                      
                      <div className="flex items-center justify-between gap-4 bg-muted/50 p-3 rounded-md">
                        <div className="flex items-start gap-3 flex-1">
                          <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                          <div className="space-y-1">
                            <Label 
                              htmlFor={`executor-access-${index}`}
                              className="text-sm font-medium cursor-pointer"
                            >
                              Allow executor to view client information
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              Grant this executor permission to access and view all client details
                            </p>
                          </div>
                        </div>
                        <Switch
                          id={`executor-access-${index}`}
                          checked={executor.accessGranted}
                          onCheckedChange={(checked) => updateExecutor(index, "accessGranted", checked)}
                          data-testid={`switch-executor-access-${index}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                  </div>
                </>
              )}
            </div>
          </form>
        </ScrollArea>
        <DialogFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => onOpenChange(false)} 
            disabled={isPending}
            data-testid="button-cancel"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            form="client-form" 
            disabled={isPending}
            data-testid="button-submit"
          >
            {isPending ? "Saving..." : (editMode ? "Save Changes" : "Create Client")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
